import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from '../../../core/components/atoms/Toast';
import {
  joinOnboardingWebinar,
  OnboardingJoinResponse,
} from '../queries/onboarding';

/**
 * Fluxo do botão "Entrar" do onboarding (ver doc backend, seções 3.2 e 6):
 * chama `POST /aprovaonboarding/webinar/join` (que marca presença) e abre o Zoom
 * pela URL retornada.
 *
 * O app não usa Zoom Web SDK — abrimos o webinar por URL em nova aba, igual ao
 * restante da plataforma.
 *
 * NÃO escondemos o card na hora: o backend marca a presença e, no próximo
 * login, `onboardingAttended` vem `true` e o bloco some sozinho. Assim o aluno
 * pode reentrar (Zoom não abriu, fechou a aba etc.) sem perder o acesso.
 */
export function useJoinOnboarding() {
  const mutation = useMutation({
    mutationKey: ['join-onboarding'],
    mutationFn: (onboardingId: string) => joinOnboardingWebinar(onboardingId),
  });

  function openZoom(data: OnboardingJoinResponse) {
    // Igual à monitoria: abrimos o webinar como participante pela URL `/w/?tk=`.
    // O backend não envia mais `webinarUrl` (era o start_url/host, com `zak`),
    // então nunca há risco de o aluno entrar como host.
    const link = `https://zoom.us/w/${data.webinarId}?tk=${data.zoomToken}`;

    const win = window.open(link, '_blank');
    win?.focus();
  }

  function handleError(error: unknown) {
    const status = (error as AxiosError)?.response?.status;

    if (status === 409) {
      toast({
        variant: 'warning',
        title: 'Sala indisponível',
        message:
          'A sala ainda não está disponível. Tente novamente próximo do horário.',
      });
      return;
    }

    if (status === 403) {
      toast({
        variant: 'error',
        title: 'Não inscrito',
        message: 'Você não está inscrito neste onboarding.',
      });
      return;
    }

    toast({
      variant: 'error',
      title: 'Erro ao entrar',
      message: 'Não foi possível entrar na sala.',
    });
  }

  async function joinOnboarding(onboardingId: string): Promise<boolean> {
    try {
      const data = await mutation.mutateAsync(onboardingId);
      openZoom(data);

      // A presença já foi marcada no backend. Não escondemos o card aqui —
      // ele some no próximo login, quando `onboardingAttended` vier `true`.
      return true;
    } catch (error) {
      handleError(error);
      return false;
    }
  }

  return {
    joinOnboarding,
    isLoading: mutation.isPending,
  };
}
